import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Switch, useTheme } from '@mui/material';
import { Button, Label } from '@components/index';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { EyeIcon } from '@icons/EyeIcon';
import type { ExecutionStatus, UsageWorkflowDTO } from '@repositories/workflowRepository/workflow.types';
import {
  useActivateUsageWorkflow,
  useDeactivateUsageWorkflow,
  useExecuteUsageWorkflow,
} from '@queryHooks/useSubscription';
import { dateTimeFormat } from '@utils/dateUtil';

const I18N_PREFIX = 'ConnectionDetail.usageWorkflow';

const getExecutionStatusColor = (status?: ExecutionStatus) => {
  if (status === 'COMPLETED') return 'green';
  if (status === 'FAILED' || status === 'STOPPED') return 'red';
  return 'gray';
};

interface Props {
  usageWorkflow?: UsageWorkflowDTO;
  subscriptionId: string;
}

export const UsageWorkflowInfo = ({ usageWorkflow, subscriptionId }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  const { mutate: activate, isPending: isActivating } = useActivateUsageWorkflow();
  const { mutate: deactivate, isPending: isDeactivating } = useDeactivateUsageWorkflow();
  const { mutate: executeWorkflow, isPending: isExecuting } = useExecuteUsageWorkflow();

  const isActive = usageWorkflow?.status === 'ACTIVE';
  const isMutating = isActivating || isDeactivating;

  const handleToggleUsageWorkflow = useCallback(() => {
    const willActivate = !isActive;
    const action = willActivate
      ? { mutateFn: activate, confirmKey: 'activateConfirm', successKey: 'activateSuccess', errorKey: 'activateError' }
      : {
          mutateFn: deactivate,
          confirmKey: 'deactivateConfirm',
          successKey: 'deactivateSuccess',
          errorKey: 'deactivateError',
        };

    alertStore.open({
      title: t(`${I18N_PREFIX}.title`),
      message: t(`${I18N_PREFIX}.${action.confirmKey}`),
      useCancel: true,
      confirmName: t('Member_User_24'),
      onConfirm: () => {
        action.mutateFn(subscriptionId, {
          onSuccess: () => {
            toastStore.open({ message: t(`${I18N_PREFIX}.${action.successKey}`), hideAction: true });
          },
          onError: () => {
            toastStore.open({ message: t(`${I18N_PREFIX}.${action.errorKey}`), hideAction: true });
          },
        });
      },
    });
  }, [isActive, activate, deactivate, subscriptionId, alertStore, toastStore, t]);

  const handleExecuteUsageWorkflow = useCallback(() => {
    alertStore.open({
      title: t(`${I18N_PREFIX}.title`),
      message: t(`${I18N_PREFIX}.executeConfirm`),
      useCancel: true,
      confirmName: t('Member_User_24'),
      onConfirm: () => {
        executeWorkflow(subscriptionId, {
          onSuccess: ({ usageWorkflow: result }) => {
            const statusText = result.latestExecutionStatus ? ` (${result.latestExecutionStatus})` : '';
            toastStore.open({
              message: `${t(`${I18N_PREFIX}.executeSuccess`)}${statusText}`,
              hideAction: true,
            });
          },
          onError: () => {
            toastStore.open({ message: t(`${I18N_PREFIX}.executeError`), hideAction: true });
          },
        });
      },
    });
  }, [executeWorkflow, subscriptionId, alertStore, toastStore, t]);

  return (
    <Container>
      <TitleRow>
        <Title>{t(`${I18N_PREFIX}.title`)}</Title>
        {usageWorkflow && (
          <Button
            variant="contain"
            size="small"
            paddingHorizontal={16}
            onClick={handleExecuteUsageWorkflow}
            disabled={isExecuting || !isActive}
          >
            {t(`${I18N_PREFIX}.executeButton`)}
          </Button>
        )}
      </TitleRow>
      <DetailWrapper>
        <InfoRow>
          <InfoName>
            <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
            {t(`${I18N_PREFIX}.schedule`)}
          </InfoName>
          <InfoValue>{usageWorkflow ? usageWorkflow.cronExpression : t(`${I18N_PREFIX}.notInstalled`)}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoName>
            <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
            {t(`${I18N_PREFIX}.timezone`)}
          </InfoName>
          <InfoValue>{usageWorkflow ? usageWorkflow.cronTimeZone : t(`${I18N_PREFIX}.notInstalled`)}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoName>
            <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
            {t(`${I18N_PREFIX}.status`)}
          </InfoName>
          <FlexBox>
            {usageWorkflow && (
              <Switch checked={isActive} onChange={handleToggleUsageWorkflow} disabled={isMutating} size="small" />
            )}
            <Label variant="contain" color={usageWorkflow ? (isActive ? 'green' : 'red') : 'gray'}>
              {usageWorkflow ? usageWorkflow.status : t(`${I18N_PREFIX}.notInstalled`)}
            </Label>
          </FlexBox>
        </InfoRow>
        <InfoRow>
          <InfoName>
            <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
            {t(`${I18N_PREFIX}.lastCollection`)}
          </InfoName>
          <FlexBox>
            <Label variant="contain" color={getExecutionStatusColor(usageWorkflow?.latestExecutionStatus)}>
              {usageWorkflow ? usageWorkflow.latestExecutionStatus : t(`${I18N_PREFIX}.notInstalled`)}
            </Label>
            <InfoValue>
              {usageWorkflow?.latestExecutionTime ? dateTimeFormat(usageWorkflow?.latestExecutionTime) : ''}
            </InfoValue>
          </FlexBox>
        </InfoRow>
        {usageWorkflow?.latestExecutionStatusMessage && (
          <>
            <InfoRow>
              <InfoName>
                <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t(`${I18N_PREFIX}.statusMessage`)}
              </InfoName>
            </InfoRow>
            <StatusMessage>{usageWorkflow?.latestExecutionStatusMessage}</StatusMessage>
          </>
        )}
      </DetailWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const Title = styled.strong`
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const DetailWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 28px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-lighter']};
  border: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  border-radius: 5px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InfoName = styled.span`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
`;

const InfoValue = styled.span`
  ${fonts.Headline8};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const FlexBox = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusMessage = styled.span`
  ${fonts.Headline8};
`;
